import React, { useEffect, useReducer, useRef } from "react";

import PropTypes from "prop-types";
import TextLink from "theme/components/text/TextLink";
import { buildQueryString } from "core-web/libs/GrebbCommerceAPI/util";
import clear from "core-web/state/models/Search/dispatches/clear";
import cloneDeep from "core-web/utils/cloneDeep";
import getQueryParams from "core-web/utils/getQueryParams";
import hexWithOpacity from "core-web/utils/hexWithOpacity";
import { inServer } from "core-web/constants";
import search from "core-web/state/models/Search/dispatches/search";
import styleObjectToProps from "core-web/utils/styleObjectToProps";
import styled from "theme/libs/styled";
import useAboveBreakpoint from "core-web/hooks/useAboveBreakpoint.js";
import { useHistory } from "react-router-dom";
import useOutsideClick from "core-web/hooks/useOutsideClick";
import { useTranslation } from "react-i18next";

const Wrapper = styled("div")`
    position: relative;
    display: flex;
    align-items: center;
    width: 100%;
    cursor: text;
`;

const HiddenPlaceholder = styled("div")`
    position: absolute;
    white-space: pre;

    & > span {
        opacity: 0;
    }
`;

const SearchTextInput = styled("input")`
    height: 1.2em;
    width: 100%;
    background: inherit;
    border: none;
    border-radius: 0;
    box-shadow: none;

    ::placeholder {
        color: ${({ theme }) => hexWithOpacity(theme.colors.black, 60)};
    }

    :focus {
        outline: none;
    }

    &[type="search"] {
        -webkit-appearance: none;
        appearance: none;
    }

    /* clears the 'X' from Chrome */
    ::-webkit-search-decoration,
    ::-webkit-search-cancel-button,
    ::-webkit-search-results-button,
    ::-webkit-search-results-decoration {
        display: none;
    }
`;

const EditButton = styled(TextLink)`
    display: inline-block;
    margin-left: 12px;
    color: ${({ theme }) => theme.colors.black_50};
    white-space: nowrap;

    ${({ theme }) => theme.below.tabletLg} {
        position: absolute;
        bottom: -28px;
        margin: 0;
        left: 0;
    }
`;

const initialState = ({ searchString }) => {
    return {
        localSearchString: searchString || "",
        isFocused: false,
    };
};

const reducer = (state, action) => {
    switch (action.type) {
        case "focus":
            return {
                ...state,
                isFocused: true,
            };
        case "blur": {
            return {
                ...state,
                isFocused: false,
            };
        }
        case "update":
            return {
                localSearchString: action.payload,
                isFocused: true,
            };
        case "clear":
            return {
                isFocused: true,
                localSearchString: "",
            };
        default:
            return state;
    }
};

const SearchInput = ({ location, placeholderText, searchString }) => {
    const history = useHistory();
    const { t } = useTranslation();
    const [state, dispatch] = useReducer(reducer, { searchString }, initialState);
    const isAboveTabletLg = useAboveBreakpoint("tabletLg");

    const wrapperRef = useRef(null);
    const hiddenRef = useRef(null);
    const inputRef = useRef(null);
    const searchTimeout = useRef(null);
    const displayedSearchString = state.localSearchString !== null ? state.localSearchString : searchString;
    const hideEdit = state.isFocused && !state.localSearchString.length;

    const inputTextStyles = styleObjectToProps([
        "Myriad Pro/40-120-semibold",
        null,
        null,
        null,
        "Myriad Pro/64-100-semibold",
    ]);

    const siteSearch = (query) => {
        const params = getQueryParams(location);
        let locationClone = cloneDeep(location);

        if (Object.hasOwn(params, "page") && !inServer) {
            const updatedQueryParams = `?${buildQueryString(params)}`;
            locationClone = { ...locationClone, search: updatedQueryParams };
        }
        search(query, history, locationClone);

        if (query && query.length < 1) {
            clear(history, location);
        }
    };

    useEffect(() => {
        if (state.localSearchString !== searchString) {
            clearTimeout(searchTimeout.current);

            searchTimeout.current = setTimeout(() => {
                siteSearch(state.localSearchString);
            }, 500);

            return () => clearTimeout(searchTimeout.current);
        }
    }, [state.localSearchString]);

    useEffect(() => {
        dispatch({ type: "update", payload: searchString });
    }, [searchString]);

    useOutsideClick(wrapperRef, () => {
        if (document.activeElement !== inputRef.current) {
            dispatch({ type: "blur" });
        }
    });

    const handleOnChange = ({ target }) => {
        const trimmedValue = target.value.replace(/^\s+/, "");
        dispatch({ type: "update", payload: trimmedValue });
    };

    //helps prevent input field being unfocused when button is clicked and lets button know that input field is focused.
    const handleEditClick = (e) => {
        e.preventDefault();

        if (state.isFocused) {
            dispatch({ type: "clear" });
        } else {
            dispatch({ type: "focus" });
        }

        inputRef.current.focus();
    };

    const handleOnFocus = () => {
        dispatch({ type: "focus" });
        inputRef.current.focus();
    };

    return (
        <Wrapper ref={wrapperRef} onClick={handleOnFocus}>
            <HiddenPlaceholder ref={hiddenRef} {...inputTextStyles}>
                <span>{displayedSearchString || placeholderText || ""}</span>
                {/* {!hideEdit && (
                    <EditButton onClick={handleEditClick}>
                        {state.isFocused ? t("search.clear_search") : t("search.edit_search")}
                    </EditButton>
                )} */}
            </HiddenPlaceholder>
            <SearchTextInput
                autoFocus
                as={isAboveTabletLg ? "input" : "textarea"}
                ref={inputRef}
                autoComplete="off"
                type="search"
                // Sets correct action label on virtual keyboards.
                enterkeyhint="search"
                placeholder={placeholderText}
                value={displayedSearchString}
                onChange={handleOnChange}
                {...inputTextStyles}
            />
        </Wrapper>
    );
};

SearchInput.propTypes = {
    location: PropTypes.object.isRequired,
    placeholderText: PropTypes.string,
    searchString: PropTypes.string.isRequired,
};

SearchInput.defaultProps = {
    placeholderText: "",
};

export default SearchInput;
